import { z } from 'zod';
import {
  OrganizationOnboardingContactValidationDto,
  OrganizationOnboardingSocialMediaValidationDto,
} from './onboarding';
import { Currency, OrganizationStatus, OrganizationTypeEnum } from '../enums';
import { CountriesEnum } from './countries';
import { RebrandThemeDto } from './rebrand';
import {
  isHttpUrl,
  isPlausiblePhoneNumber,
  PUBLIC_PAGE_SOCIAL_TYPES,
  type PublicPageSectionKey,
  WorkingHoursSettingsDto,
} from './publicPage';
import { IanaTimeZoneDto } from './timeZone';

/**
 * Task 11 (membership-renewal plan, Decision 8) — the lead days an
 * organization may pick for its membership reminders, in the order they are
 * stored and shown (most days first). Mirrored by the database CHECK
 * `organizations_membership_reminder_days_check` (migration 20260925170000).
 */
export const MEMBERSHIP_REMINDER_LEAD_DAYS = [7, 3, 1] as const;
export type MembershipReminderLeadDay =
  (typeof MEMBERSHIP_REMINDER_LEAD_DAYS)[number];

/**
 * A subset of `MEMBERSHIP_REMINDER_LEAD_DAYS`, each day at most once, stored
 * sorted descending; the empty set turns the organization's reminders off.
 */
export const MembershipReminderDaysDto = z
  .array(z.union([z.literal(7), z.literal(3), z.literal(1)]))
  .max(MEMBERSHIP_REMINDER_LEAD_DAYS.length, 'VALIDATION__NotAllowed')
  .refine(
    (days) => new Set(days).size === days.length,
    'VALIDATION__NotAllowed',
  )
  .transform((days) => [...days].sort((a, b) => b - a));

export const UpdateOrganizationDto = z
  .object({
    // Trimmed BEFORE it is measured: `"   "` used to pass `.min(3)` and reach
    // the row (F6, docs/review/bugz-5.md). Same rule in the frontend schemas.
    name: z
      .string()
      .trim()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit')
      .or(z.undefined()),
    isPublic: z.boolean().optional(),
    // T12 (spec §4.4, D7) — the ONE field of an organization whose value is
    // decided at creation and afterwards changed only by hand, under the
    // existing `organizations_update` permission. Nothing derives it here: an
    // address change in the same PATCH does not move it, because money already
    // recorded in the old currency does not move either.
    defaultCurrency: z.nativeEnum(Currency).optional(),
    // Foundation 1 D3 — the owner's correction, needed for countries with several
    // zones. `IanaTimeZoneDto` is the cheap strict gate; the controller then calls
    // `OrganizationTimezoneService.assertSupported`, because JS and this
    // PostgreSQL build do not know exactly the same names.
    timezone: IanaTimeZoneDto.optional(),
    // Task 11 (membership-renewal plan) — the owner's choice of reminder lead
    // days; see `MembershipReminderDaysDto`. Same `organizations_update`
    // permission as every other setting here.
    membershipReminderDays: MembershipReminderDaysDto.optional(),
    // Task 9 (Q5, Ruling R14) — the organization's tax id AS IT APPEARS ON ITS
    // PAYMENT CONFIRMATIONS. ONE free-form field on purpose: the product is
    // country-agnostic and integrates with no e-invoicing system, so there is
    // no per-country field set to validate against and nothing here may reject
    // a legitimate foreign identifier. Trimmed, capped, and `null` clears it.
    // It is captured into `Invoices.buyerSnapshot` at issue — changing it never
    // rewrites a document already issued.
    billingTaxId: z
      .string()
      .trim()
      .max(255, 'VALIDATION__Max255CharLimit')
      .nullable()
      .optional()
      .transform((value) => (value === '' ? null : value)),
    email: z
      .string()
      .trim()
      .email()
      .optional()
      .nullable()
      .transform((val) => (val === '' ? undefined : val)),
    address: z
      .object({
        country: CountriesEnum.optional()
          .or(z.literal(''))
          .transform((e) => (!e ? undefined : e)),
        street: z
          .string()
          .optional()
          .transform((e) => (!e || e.trim() === '' ? undefined : e))
          .refine((e) => !e || e.length >= 3, 'VALIDATION__Min3CharLimit'),
        city: z
          .string()
          .optional()
          .transform((e) => (!e || e.trim() === '' ? undefined : e))
          .refine((e) => !e || e.length >= 3, 'VALIDATION__Min3CharLimit'),
        zip: z
          .string()
          .optional()
          .transform((e) => (!e || e.trim() === '' ? undefined : e))
          .refine((e) => !e || e.length >= 3, 'VALIDATION__Min3CharLimit'),
        isAddressPublic: z.boolean().optional(),
      })
      .transform((obj) => {
        const isEmpty = Object.values(obj).every((v) => v === undefined);
        return isEmpty ? undefined : obj;
      }),
    contact: OrganizationOnboardingContactValidationDto.optional(),
    socialMedia: OrganizationOnboardingSocialMediaValidationDto.optional(),
    logoName: z.string().nullable().optional(),
  })
  .partial()
  .transform((obj) => {
    const isEmpty = Object.values(obj).every((v) => v === undefined);
    return isEmpty ? undefined : obj;
  });

export type ApiUpdateOrganizationDto = z.infer<typeof UpdateOrganizationDto>;

export const OrganizationDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().trim().email().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  isPublic: z.boolean().optional(),
  status: z.string().toUpperCase().pipe(z.nativeEnum(OrganizationStatus)),
  type: OrganizationTypeEnum,
  tenantId: z.string().uuid(),
});

export type ApiOrganizationDto = z.infer<typeof OrganizationDto>;

// F9 — leave/close orchestration: optional content-transfer target (owner
// must be an ACTIVE owner of the target org, enforced BE-side). The
// `preprocess` wrapper (same pattern as `DisconnectClientDto`, F10) handles
// a request with no body at all (no Content-Type -> Express leaves
// `req.body` as `undefined`, not `{}`), so pre-F9 callers keep working.
export const LeaveOrganizationDto = z.preprocess(
  (value) => value ?? {},
  z
    .object({
      // transfer (move) authored content into another org the caller owns
      contentTargetOrganizationId: z.string().uuid().optional(),
      // copy (duplicate) authored content into another org — originals stay
      contentCopyTargetOrganizationId: z.string().uuid().optional(),
    })
    .strict()
    .refine(
      (dto) =>
        !(
          dto.contentTargetOrganizationId && dto.contentCopyTargetOrganizationId
        ),
      { message: 'VALIDATION__TransferOrCopyNotBoth' },
    ),
);
export type ApiLeaveOrganizationDto = z.infer<typeof LeaveOrganizationDto>;

// Rebrand (F3): per-org theme color overrides. The body shape is the shared
// `RebrandThemeDto` (see ./rebrand — the two brand colours as strict 6-hex,
// plus the `glow`, `surface` and `calendarColours` settings). The
// org endpoint FULL-REPLACES the stored theme with the validated payload (no
// server-side deep-merge). These org-named aliases are kept for the existing
// controller/itest imports; the schema itself is not duplicated.
export const UpdateOrganizationRebrandThemeDto = RebrandThemeDto;

export type ApiUpdateOrganizationRebrandThemeDto = z.infer<
  typeof UpdateOrganizationRebrandThemeDto
>;

// WS2 — standalone content COPY (library/nutrition/education). Selected ids
// must be the caller's own content in the source org; the target org needs
// an ACTIVE OWNER/TRAINER/STAFF membership (both enforced BE-side).
const copyContentIds = z.array(z.string().uuid()).max(500).optional();
export const CopyContentDto = z
  .object({
    targetOrganizationId: z.string().uuid(),
    selection: z
      .object({
        workoutIds: copyContentIds,
        programIds: copyContentIds,
        exerciseIds: copyContentIds,
        mealIds: copyContentIds,
        nutritionPlanIds: copyContentIds,
        groceryIds: copyContentIds,
        courseIds: copyContentIds,
        moduleIds: copyContentIds,
        episodeIds: copyContentIds,
      })
      .strict(),
  })
  .strict();
export type ApiCopyContentDto = z.infer<typeof CopyContentDto>;

// Copying one LIBRARY item into an organization the caller works in. The body
// names the LIBRARY ROW, never the content: the row is what proves the caller
// acquired the content, and it is the only shape that covers both a personal
// copy (which the caller owns) and a legacy row (which still points at the
// SELLER's original, and which the caller does not own).
//
// `:organizationId` on this route is the TARGET, unlike its `content-copy`
// sibling where it is the SOURCE — there is no source organization here.
export const CopyLibraryContentDto = z
  .object({
    libraryId: z.string().uuid(),
  })
  .strict();
export type ApiCopyLibraryContentDto = z.infer<typeof CopyLibraryContentDto>;
export const UpdateOrganizationCategoryTagsDto = z.object({
  tagIds: z
    .array(z.string().uuid())
    .max(50)
    .refine((ids) => new Set(ids).size === ids.length, {
      message: 'VALIDATION__DuplicateTagIds',
    }),
});
export type ApiUpdateOrganizationCategoryTagsDto = z.infer<
  typeof UpdateOrganizationCategoryTagsDto
>;

export const UpdateOrganizationBasicInfoDto = z
  .object({
    name: z
      .string()
      .trim()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit'),
    logoName: z.string().nullable().optional(),
    about: z
      .string()
      .trim()
      .max(5000, 'VALIDATION__Max5000CharLimit')
      .nullable()
      .transform((value) => (value === '' ? null : value)),
    // Two relations, two catalogs, never mixed: the organization TYPE
    // (ORGANIZATION_TYPE) and its own EXPERTISE (the EXPERTISE catalog
    // trainers use too). Each replaces its whole set.
    organizationTypeTagIds: UpdateOrganizationCategoryTagsDto.shape.tagIds,
    expertiseTagIds: UpdateOrganizationCategoryTagsDto.shape.tagIds,
  })
  .strict();
export type ApiUpdateOrganizationBasicInfoDto = z.infer<
  typeof UpdateOrganizationBasicInfoDto
>;

export const UpdateOrganizationClientTermsDto = z
  .object({ requireClientTermsOnConnect: z.boolean() })
  .strict();
export type ApiUpdateOrganizationClientTermsDto = z.infer<
  typeof UpdateOrganizationClientTermsDto
>;

export const UpdateOrganizationModulesDto = z
  .object({ deskEnabled: z.boolean(), lockerKeysEnabled: z.boolean() })
  .strict();
export type ApiUpdateOrganizationModulesDto = z.infer<
  typeof UpdateOrganizationModulesDto
>;

export const UpdateOrganizationTrainerTermsDto = z
  .object({ trainersMayHaveOwnTerms: z.boolean() })
  .strict();
export type ApiUpdateOrganizationTrainerTermsDto = z.infer<
  typeof UpdateOrganizationTrainerTermsDto
>;

const clearableText = z
  .string()
  .trim()
  .max(255, 'VALIDATION__Max255CharLimit')
  .nullable()
  .transform((value) => (value === '' ? null : value));

export const UpdateOrganizationContactDto = z
  .object({
    email: z
      .string()
      .trim()
      .email('VALIDATION__InvalidEmail')
      .or(z.literal(''))
      .nullable()
      .transform((value) => value || null),
    // 11.13 — whether the email is shown on the public page. Omitted = left
    // as stored (never silently flipped).
    isEmailPublic: z.boolean().optional(),
    phone: z
      .object({
        phoneNumber: z
          .string()
          .trim()
          .min(3, 'VALIDATION__Min3CharLimit')
          .max(255, 'VALIDATION__Max255CharLimit'),
        phoneCountryCode: CountriesEnum,
        isPhoneNumberPublic: z.boolean(),
      })
      .strict()
      .nullable(),
    address: z
      .object({
        street: clearableText,
        city: clearableText,
        state: clearableText,
        zip: clearableText,
        country: CountriesEnum.nullable(),
        isAddressPublic: z.boolean(),
      })
      .strict()
      .nullable()
      .transform((address) =>
        address &&
        [
          address.street,
          address.city,
          address.state,
          address.zip,
          address.country,
        ].some((value) => value !== null)
          ? address
          : null,
      ),
    defaultCurrency: z.nativeEnum(Currency),
    billingTaxId: clearableText,
    companyRegistrationNumber: clearableText,
  })
  .strict();
export type ApiUpdateOrganizationContactDto = z.infer<
  typeof UpdateOrganizationContactDto
>;

// An owner-authored link that renders as a raw `href` on the anonymous
// public page — http(s) only, the same rule as the public page's own editor.
const publicHttpUrl = z
  .string()
  .trim()
  .max(255, 'VALIDATION__Max255CharLimit')
  .refine(isHttpUrl, 'VALIDATION__InvalidUrl');

// Organization Profile v2 — the "Social & marketing" section, saved as ONE
// unit (`PUT /organizations/:id/profile/social`). Full-section write: the
// lists REPLACE what is stored, `null` removes the website.
//
//   isPublic — the WHOLE section is public or not: the org public page's
//              `social` section visibility, written to every link and to the
//              website too, so no single row can say otherwise.
//   website  — the organization's `WEBSITE` contact channel.
//   links    — its `SocialMedia` rows, at most one per network (X is
//              `TWITTER`). Their order in the array is their public order.
export const UpdateOrganizationSocialDto = z
  .object({
    isPublic: z.boolean(),
    website: z.object({ url: publicHttpUrl }).strict().nullable(),
    links: z
      .array(
        z
          .object({
            type: z.enum(PUBLIC_PAGE_SOCIAL_TYPES),
            url: publicHttpUrl,
          })
          .strict(),
      )
      .max(PUBLIC_PAGE_SOCIAL_TYPES.length)
      .refine(
        (links) =>
          new Set(links.map((link) => link.type)).size === links.length,
        { message: 'VALIDATION__DuplicateSocialType' },
      ),
  })
  .strict();
export type ApiUpdateOrganizationSocialDto = z.infer<
  typeof UpdateOrganizationSocialDto
>;

// A list edited as a whole: rows that carry an `id` update that row, rows
// without one are new, stored rows missing from the list are removed. An id
// may appear once.
const uniqueIds = <T extends { id?: string }>(rows: T[]) => {
  const ids = rows.map((row) => row.id).filter((id) => id !== undefined);
  return new Set(ids).size === ids.length;
};

// How the org shows its prices: its own price list, or a link to the pricing
// page on its own website. Stored as the org page's `pricing` section
// settings; the list is kept while the link is chosen, so switching back
// loses nothing.
export const PRICING_DISPLAY_MODES = ['ITEMS', 'LINK'] as const;
export type PricingDisplayMode = (typeof PRICING_DISPLAY_MODES)[number];

export const PricingSettingsDto = z
  .object({
    mode: z.enum(PRICING_DISPLAY_MODES),
    url: publicHttpUrl.nullable(),
  })
  .strict()
  .refine((settings) => settings.mode !== 'LINK' || !!settings.url, {
    message: 'VALIDATION__Required',
    path: ['url'],
  });
export type ApiPricingSettingsDto = z.infer<typeof PricingSettingsDto>;

const salesContactFields = {
  name: z
    .string()
    .trim()
    .min(1, 'VALIDATION__Required')
    .max(255, 'VALIDATION__Max255CharLimit'),
  email: z
    .string()
    .trim()
    .email('VALIDATION__InvalidEmail')
    .or(z.literal(''))
    .nullable()
    .transform((value) => value || null),
  phone: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || isPlausiblePhoneNumber(value),
      'VALIDATION__InvalidPhoneNumber',
    )
    .nullable()
    .transform((value) => value || null),
};

// The org page sections the Business details section is made of. They are
// public TOGETHER — one flag for all of them (see
// `UpdateOrganizationBusinessDetailsDto`); the org's Public page panel shows
// them as a single "Business details" row, `hours` standing for the group.
export const BUSINESS_DETAILS_SECTION_KEYS = [
  'hours',
  'locations',
  'pricing',
] as const satisfies readonly PublicPageSectionKey[];

// Organization Profile v2 — the "Business details" section, saved as ONE unit
// (`PUT /organizations/:id/profile/business`). Full-section write: every list
// REPLACES what is stored (matched by `id`, see `uniqueIds`), `null` clears.
// The section is public as a WHOLE (`isPublic`): that flag is the visibility
// of every org page section in `BUSINESS_DETAILS_SECTION_KEYS` and is written
// to every row of the section as well, so the public page shows all of it or
// none of it.
//
//   hours        — the org public page's `hours` section settings.
//   locations    — its public-page locations: `Addresses` rows that are NOT
//                  the legal address (`isPrimary: false`); the legal address
//                  is the Contact & legal section's and is never touched here.
//   pricing      — `ServiceOfferings` rows (title, description, one price)
//                  plus the `pricing` section settings (`mode`, `url`). The
//                  currency is not sent — it is always the organization's
//                  `defaultCurrency`.
//   salesContact — the one person to contact for sales, stored as the org's
//                  `ContactChannels` rows with `purpose: 'SALES'`.
export const UpdateOrganizationBusinessDetailsDto = z
  .object({
    isPublic: z.boolean(),
    hours: WorkingHoursSettingsDto.nullable(),
    locations: z
      .array(
        z
          .object({
            id: z.string().uuid().optional(),
            label: z
              .string()
              .trim()
              .max(60, 'VALIDATION__Max60CharLimit')
              .nullable()
              .transform((value) => (value === '' ? null : value)),
            street: clearableText,
            city: clearableText,
            state: clearableText,
            zip: clearableText,
            country: CountriesEnum.nullable(),
          })
          .strict(),
      )
      .max(20)
      .refine(uniqueIds, { message: 'VALIDATION__DuplicateIds' }),
    pricing: z
      .object({
        settings: PricingSettingsDto,
        items: z
          .array(
            z
              .object({
                id: z.string().uuid().optional(),
                title: z
                  .string()
                  .trim()
                  .min(1, 'VALIDATION__Required')
                  .max(120, 'VALIDATION__Max120CharLimit'),
                description: z
                  .string()
                  .trim()
                  .max(1000)
                  .nullable()
                  .transform((value) => (value === '' ? null : value)),
                price: z.number().min(0).nullable(),
              })
              .strict(),
          )
          .max(50)
          .refine(uniqueIds, { message: 'VALIDATION__DuplicateIds' }),
      })
      .strict(),
    salesContact: z.object(salesContactFields).strict().nullable(),
  })
  .strict();
export type ApiUpdateOrganizationBusinessDetailsDto = z.infer<
  typeof UpdateOrganizationBusinessDetailsDto
>;

// `PUT /organizations/:id/profile/business/visibility` — the Public page
// panel's switch for the whole Business details section, without resending
// its content.
export const UpdateOrganizationBusinessDetailsVisibilityDto = z
  .object({ isPublic: z.boolean() })
  .strict();
export type ApiUpdateOrganizationBusinessDetailsVisibilityDto = z.infer<
  typeof UpdateOrganizationBusinessDetailsVisibilityDto
>;

// `GET /organizations/:id/profile/sales-contact` — the SALES channels folded
// back into the one contact the Business details section edits. Its
// visibility is the section's, not its own.
export type ApiOrganizationSalesContact = {
  name: string;
  email: string | null;
  phone: string | null;
};

export const CloseOrganizationDto = z.preprocess(
  (value) => value ?? {},
  z
    .object({
      // transfer (move) authored content into another org the caller owns
      contentTargetOrganizationId: z.string().uuid().optional(),
      // copy (duplicate) authored content into another org — originals stay
      contentCopyTargetOrganizationId: z.string().uuid().optional(),
      // F10 semantics reused as-is: default true = clients keep access to
      // content already assigned via Schedule (unchanged prior behavior).
      allowClientContentAccess: z.boolean().default(true),
    })
    .strict()
    .refine(
      (dto) =>
        !(
          dto.contentTargetOrganizationId && dto.contentCopyTargetOrganizationId
        ),
      { message: 'VALIDATION__TransferOrCopyNotBoth' },
    ),
);
export type ApiCloseOrganizationDto = z.infer<typeof CloseOrganizationDto>;
